import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Square, Circle, ZoomIn, ZoomOut, Maximize, ImageOff } from 'lucide-react';
import { cn } from '@/lib/utils';

const CENTRALIZED_KEYPAD_FILL_COLOR = '#1d4ed8';
const CENTRALIZED_KEYPAD_BORDER_COLOR = '#1e3a8a';
const CENTRALIZED_KEYPAD_TEXT_COLOR = '#ffffff';

const getCentralizedKeypadFontSize = (label: string, markerSize: number) => {
  if (label.length <= 2) return Math.max(8, markerSize * 0.45);
  if (label.length === 3) return Math.max(7, markerSize * 0.38);
  return Math.max(6, markerSize * 0.3);
};

// Simplified types for public view (matching edge function response)
interface PublicFixture {
  id: string;
  custom_name?: string;
  x_position: number;
  y_position: number;
  fixture_type: string;
  rotation?: number;
  scale?: number;
  category?: string;
  subcategory?: string;
  category_info?: { category?: string; subcategory?: string };
}

interface PublicControl {
  id: string;
  name?: string;
  custom_name?: string;
  x_position: number;
  y_position: number;
  type: string;
  sequence_number?: number;
}

interface PublicRoom {
  id: string;
  name: string;
  room_type: string;
  points: Array<{ x: number; y: number }>;
}

interface PublicPanel {
  id: string;
  name: string;
  x_position: number;
  y_position: number;
}

interface PublicFloorPlan {
  id: string;
  name: string;
  image_url: string;
  scale_data?: {
    pixelsPerFoot?: number;
    pixelsPerMeter?: number;
    unit?: 'feet' | 'meters';
  };
}

interface PublicFloorPlanCanvasProps {
  floorPlan: PublicFloorPlan;
  fixtures: PublicFixture[];
  controls: PublicControl[];
  rooms: PublicRoom[];
  panels: PublicPanel[];
}

// Helper to get control color (matching ControlLayer logic)
const getControlColor = (controlType: string) => {
  switch (controlType) {
    case 'keypad':
      return '#3b82f6'; // Blue-500
    case 'dimmer':
      return '#fb923c'; // Orange-400
    case 'switch':
      return '#22c55e'; // Green-500
    case 'auxiliary':
      return '#a16207'; // Yellow-700
    case 'centralized-keypad':
    case 'lv-keypad':
      return CENTRALIZED_KEYPAD_BORDER_COLOR;
    default:
      return '#6b7280'; // Gray-500
  }
};

// Helper to get control background color (matching ControlLayer logic)
const getControlBackgroundColor = (controlType: string) => {
  if (controlType === 'lv-keypad' || controlType === 'centralized-keypad') {
    return CENTRALIZED_KEYPAD_FILL_COLOR;
  }
  return 'transparent';
};

// Helper to get control display text (matching ControlLayer logic)
const getControlIcon = (control: PublicControl, markerSize: number) => {
  let displayText = '';
  const controlType = control.type;

  switch (controlType) {
    case 'keypad':
      displayText = 'K';
      break;
    case 'centralized-keypad':
    case 'lv-keypad':
      displayText = `K${control.sequence_number || 1}`;
      break;
    case 'dimmer':
      displayText = 'D';
      break;
    case 'switch':
      displayText = 'S';
      break;
    case 'auxiliary':
      displayText = 'A';
      break;
    default:
      displayText = 'C';
      break;
  }

  const isLVKeypad = controlType === 'lv-keypad' || controlType === 'centralized-keypad';
  const textColor = isLVKeypad ? CENTRALIZED_KEYPAD_TEXT_COLOR : getControlColor(controlType);

  return (
    <div
      className="flex items-center justify-center h-full w-full font-bold"
      style={{
        fontSize: `${isLVKeypad ? getCentralizedKeypadFontSize(displayText, markerSize) : Math.max(8, markerSize * 0.4)}px`,
        color: textColor,
        lineHeight: 1,
        whiteSpace: 'nowrap',
      }}
    >
      {displayText}
    </div>
  );
};

// Helper to get room color (basic color scheme for room types)
const getRoomTypeColor = (roomType: string) => {
  switch (roomType?.toLowerCase()) {
    case 'bedroom':
      return '#93c5fd'; // blue-300
    case 'bathroom':
      return '#a5f3fc'; // cyan-300
    case 'kitchen':
      return '#fde68a'; // yellow-200
    case 'living_room':
    case 'living room':
      return '#fca5a5'; // red-300
    case 'dining_room':
    case 'dining room':
      return '#fdba74'; // orange-300
    case 'hallway':
      return '#d1d5db'; // gray-300
    case 'office':
      return '#c4b5fd'; // violet-300
    default:
      return '#d1d5db'; // gray-300
  }
};

const MIN_SCALE = 0.02;
const MAX_SCALE = 12;
const clampScale = (s: number) => Math.min(MAX_SCALE, Math.max(MIN_SCALE, s));

/**
 * VF-58: read-only canvas for the public share viewer.
 *
 * All content (image + rooms SVG + markers) lives on a "plane" sized to the
 * image's NATURAL pixel dimensions, so raw x/y positions from the DB line up
 * 1:1 — the previous version placed markers in viewport pixels over an
 * object-contain (letterboxed) image, which misaligned everything. The plane
 * is then translate/scaled to fit, which also gives us zoom (wheel, pinch,
 * buttons) and pan (drag) for free.
 */
export function PublicFloorPlanCanvas({
  floorPlan,
  fixtures,
  controls,
  rooms,
  panels,
}: PublicFloorPlanCanvasProps) {
  // Default marker sizes (matching the authenticated viewer)
  const controlMarkerSize = 20;
  const panelMarkerSize = 18;
  const roomOpacity = 0.4;

  const containerRef = useRef<HTMLDivElement>(null);
  const [natural, setNatural] = useState<{ w: number; h: number } | null>(null);
  const [imgError, setImgError] = useState(false);
  const [view, setView] = useState<{ scale: number; x: number; y: number } | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const dragStart = useRef<{ px: number; py: number; x: number; y: number } | null>(null);
  const pinchStart = useRef<{
    dist: number;
    midX: number;
    midY: number;
    view: { scale: number; x: number; y: number };
  } | null>(null);

  const fitView = useCallback(() => {
    const el = containerRef.current;
    if (!el || !natural) return;
    const cw = el.clientWidth;
    const ch = el.clientHeight;
    if (!cw || !ch) return;
    const scale = clampScale(Math.min(cw / natural.w, ch / natural.h));
    setView({
      scale,
      x: (cw - natural.w * scale) / 2,
      y: (ch - natural.h * scale) / 2,
    });
  }, [natural]);

  // Fit once the image dimensions are known (and re-fit on container resize
  // until the user has interacted — afterwards we keep their view on resize).
  const userInteracted = useRef(false);
  useEffect(() => {
    if (!natural) return;
    fitView();
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      if (!userInteracted.current) fitView();
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [natural, fitView]);

  const zoomAt = useCallback((clientX: number, clientY: number, factor: number) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const px = clientX - rect.left;
    const py = clientY - rect.top;
    userInteracted.current = true;
    setView(v => {
      if (!v) return v;
      const scale = clampScale(v.scale * factor);
      const k = scale / v.scale;
      return { scale, x: px - (px - v.x) * k, y: py - (py - v.y) * k };
    });
  }, []);

  const zoomAtCenter = useCallback(
    (factor: number) => {
      const el = containerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      zoomAt(rect.left + rect.width / 2, rect.top + rect.height / 2, factor);
    },
    [zoomAt],
  );

  // Wheel zoom needs a non-passive listener to preventDefault page scroll.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      zoomAt(e.clientX, e.clientY, e.deltaY < 0 ? 1.12 : 1 / 1.12);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [zoomAt]);

  const handlePointerDown = (e: React.PointerEvent) => {
    const el = containerRef.current;
    if (!el || !view) return;
    el.setPointerCapture(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (pointers.current.size === 1) {
      dragStart.current = { px: e.clientX, py: e.clientY, x: view.x, y: view.y };
      setIsDragging(true);
    } else if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      pinchStart.current = {
        dist: Math.hypot(a.x - b.x, a.y - b.y),
        midX: (a.x + b.x) / 2,
        midY: (a.y + b.y) / 2,
        view,
      };
      dragStart.current = null;
    }
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (pointers.current.size === 2 && pinchStart.current) {
      const [a, b] = [...pointers.current.values()];
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      if (pinchStart.current.dist > 0) {
        const el = containerRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const start = pinchStart.current;
        const scale = clampScale(start.view.scale * (dist / start.dist));
        const k = scale / start.view.scale;
        const px = start.midX - rect.left;
        const py = start.midY - rect.top;
        userInteracted.current = true;
        setView({ scale, x: px - (px - start.view.x) * k, y: py - (py - start.view.y) * k });
      }
    } else if (pointers.current.size === 1 && dragStart.current) {
      const start = dragStart.current;
      userInteracted.current = true;
      setView(v =>
        v ? { ...v, x: start.x + (e.clientX - start.px), y: start.y + (e.clientY - start.py) } : v,
      );
    }
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) pinchStart.current = null;
    if (pointers.current.size === 0) {
      dragStart.current = null;
      setIsDragging(false);
    } else if (pointers.current.size === 1 && view) {
      // Re-anchor drag with the remaining pointer
      const [p] = [...pointers.current.values()];
      dragStart.current = { px: p.x, py: p.y, x: view.x, y: view.y };
    }
  };

  if (imgError) {
    return (
      <div className="flex flex-col items-center justify-center w-full h-full bg-gray-100 text-muted-foreground gap-3">
        <ImageOff className="h-10 w-10" />
        <div className="font-medium">The floor plan image couldn&apos;t be loaded</div>
        <div className="text-sm">Try reloading the page, or contact the sender.</div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="relative w-full h-full overflow-hidden bg-gray-100 touch-none select-none"
      style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
    >
      {/* Probe image: resolves natural dimensions + surfaces load errors */}
      {!natural && (
        <img
          src={floorPlan.image_url}
          alt=""
          className="hidden"
          onLoad={e =>
            setNatural({
              w: e.currentTarget.naturalWidth || 1,
              h: e.currentTarget.naturalHeight || 1,
            })
          }
          onError={() => setImgError(true)}
        />
      )}

      {natural && view && (
        <div
          className="absolute top-0 left-0"
          style={{
            width: `${natural.w}px`,
            height: `${natural.h}px`,
            transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})`,
            transformOrigin: '0 0',
          }}
        >
          {/* Floor plan image at natural size — DB coordinates map 1:1 */}
          <img
            src={floorPlan.image_url}
            alt={floorPlan.name}
            width={natural.w}
            height={natural.h}
            draggable={false}
            className="absolute inset-0 w-full h-full"
            onError={() => setImgError(true)}
          />

          {/* Rooms Layer - SVG overlay (z-index 5) */}
          {rooms.length > 0 && (
            <svg
              className="absolute inset-0 pointer-events-none"
              style={{ zIndex: 5 }}
              width={natural.w}
              height={natural.h}
              viewBox={`0 0 ${natural.w} ${natural.h}`}
            >
              {rooms.map(room => {
                const roomColor = getRoomTypeColor(room.room_type);

                // Parse points if they're strings
                let points = room.points;
                if (typeof points === 'string') {
                  try {
                    points = JSON.parse(points);
                  } catch (_e) {
                    return null;
                  }
                }

                // Build SVG path from points
                if (!points || points.length < 3) return null;

                const pathData =
                  points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ') + ' Z';

                return (
                  <g key={room.id}>
                    {/* Room polygon */}
                    <path
                      d={pathData}
                      fill={roomColor}
                      fillOpacity={roomOpacity}
                      stroke={roomColor}
                      strokeWidth={2}
                      strokeOpacity={0.8}
                    />
                    {/* Room label */}
                    {room.name && (
                      <text
                        x={points.reduce((sum, p) => sum + p.x, 0) / points.length}
                        y={points.reduce((sum, p) => sum + p.y, 0) / points.length}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        className="select-none"
                        style={{
                          fontSize: '12px',
                          fontWeight: 600,
                          fill: '#374151',
                          pointerEvents: 'none',
                        }}
                      >
                        {room.name}
                      </text>
                    )}
                  </g>
                );
              })}
            </svg>
          )}

          {/* Fixtures Layer (z-index 10) */}
          {fixtures.length > 0 && (
            <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 10 }}>
              {fixtures.map(fixture => (
                <div
                  key={fixture.id}
                  className="absolute select-none"
                  style={{
                    left: `${fixture.x_position}px`,
                    top: `${fixture.y_position}px`,
                    transform: `translate(-50%, -50%) scale(${fixture.scale || 1.0}) rotate(${fixture.rotation || 0}deg)`,
                  }}
                >
                  <Circle
                    className="text-yellow-500"
                    style={{ width: 18, height: 18 }}
                    fill="currentColor"
                    strokeWidth={1}
                  />
                </div>
              ))}
            </div>
          )}

          {/* Controls Layer (z-index 15) */}
          {controls.length > 0 && (
            <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 15 }}>
              {controls.map(control => {
                const controlType = control.type;
                const backgroundColor = getControlBackgroundColor(controlType);
                const borderColor = getControlColor(controlType);

                return (
                  <div
                    key={control.id}
                    className={cn(
                      'absolute transition-all duration-200 border-2',
                      'flex items-center justify-center select-none',
                    )}
                    style={{
                      left: `${control.x_position}px`,
                      top: `${control.y_position}px`,
                      width: `${controlMarkerSize}px`,
                      height: `${controlMarkerSize}px`,
                      backgroundColor: backgroundColor,
                      borderColor: borderColor,
                      borderRadius: '0px', // Square corners
                      transform: 'translate(-50%, -50%)',
                    }}
                  >
                    {getControlIcon(control, controlMarkerSize)}
                  </div>
                );
              })}
            </div>
          )}

          {/* Panels Layer (z-index 11) */}
          {panels.length > 0 && (
            <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 11 }}>
              {panels.map(panel => (
                <div
                  key={panel.id}
                  className="absolute"
                  style={{
                    left: `${panel.x_position}px`,
                    top: `${panel.y_position}px`,
                    transform: 'translate(-50%, -50%)',
                  }}
                >
                  <Square
                    className="text-white"
                    style={{ width: panelMarkerSize, height: panelMarkerSize }}
                    strokeWidth={2}
                    fill="transparent"
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Zoom controls */}
      {natural && view && (
        <div className="absolute bottom-4 right-4 flex flex-col gap-1 rounded-lg border bg-white/90 p-1 shadow-md">
          <button
            type="button"
            aria-label="Zoom in"
            className="p-2 rounded hover:bg-gray-100 text-gray-700"
            onClick={() => zoomAtCenter(1.25)}
          >
            <ZoomIn className="h-4 w-4" />
          </button>
          <button
            type="button"
            aria-label="Zoom out"
            className="p-2 rounded hover:bg-gray-100 text-gray-700"
            onClick={() => zoomAtCenter(1 / 1.25)}
          >
            <ZoomOut className="h-4 w-4" />
          </button>
          <button
            type="button"
            aria-label="Fit to screen"
            className="p-2 rounded hover:bg-gray-100 text-gray-700"
            onClick={() => {
              userInteracted.current = false;
              fitView();
            }}
          >
            <Maximize className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
}
